// src/hooks/useReverseGeocode.js

import { useState, useEffect } from "react";
import { reverseGeocode } from "../utils/geocoding";
import { logError } from "../utils/logger";

/**
 * Custom hook for reverse geocoding
 * Fetches address once on mount when coordinates are provided
 *
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Object} { address, displayAddress, loading, error, refetch }
 */
export function useReverseGeocode(latitude, longitude) {
  const [address, setAddress] = useState(null);
  const [displayAddress, setDisplayAddress] = useState(null);
  const [properties, setProperties] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAddress = async () => {
    // Check if offline
    if (!navigator.onLine) {
      setAddress("Unknown Location");
      setDisplayAddress("Offline - location unavailable");
      setCandidates([]);
      setError("No network connection");
      setLoading(false);
      return;
    }

    // Validate coordinates
    if (!latitude || !longitude) {
      setAddress("Unknown Location");
      setDisplayAddress("Coordinates unavailable");
      setCandidates([]);
      setError("Invalid coordinates");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken =
        process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
        process.env.VITE_MAPBOX_TOKEN;
      const result = await reverseGeocode(latitude, longitude, accessToken);

      setAddress(result.fullAddress);
      setDisplayAddress(result.displayAddress);
      setProperties(result.properties);
      setCandidates(result.candidates || []);
      setError(null);
    } catch (error) {
      logError("[useReverseGeocode] Error:", error);
      setAddress("Unknown Location");
      setDisplayAddress("Unknown Location");
      setCandidates([]);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchAddress();
  }, []); // Empty dependency - fetch once on mount

  return {
    address,
    displayAddress,
    properties,
    candidates,
    loading,
    error,
    refetch: fetchAddress,
  };
}
